import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Box,
  Typography,
  TableSortLabel,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Slide,
  Tooltip,
  Badge,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { keyframes } from "@mui/system";
import moment from "moment";
import { getAllDocuments, getMriDocuments } from "../../../config/service";

const columnHelper = createColumnHelper();
const undoPulse = keyframes`
  0% {
    box-shadow: inset 0 0 0 0 rgba(14,165,233,0.0);
    background-color: rgba(224,242,254,0.2);
  }
  35% {
    box-shadow: inset 0 0 0 3px rgba(14,165,233,0.55);
    background-color: rgba(224,242,254,0.78);
  }
  100% {
    box-shadow: inset 0 0 0 1px rgba(14,165,233,0.20);
    background-color: rgba(224,242,254,0.36);
  }
`;
const copyIconPulse = keyframes`
  0% { transform: scale(0.9); opacity: 0.65; }
  100% { transform: scale(1); opacity: 1; }
`;

const PARTNER_ICHILOV = "64d326cd869f4f8664538775";

/** First-row QWERTY keys → status menu item index (matches STATUS_TYPES order). */
const STATUS_MENU_KEYS = ["q", "w", "e", "r", "t", "y", "u"];


function SortableHeader({
  label,
  field,
  sortField,
  sortDir,
  onSortChange,
  align = "left",
}) {
  const active = sortField === field;
  return (
    <TableSortLabel
      active={active}
      direction={active ? (sortDir === 1 ? "asc" : "desc") : "asc"}
      onClick={() => onSortChange(field)}
      sx={{
        width: align === "left" ? "auto" : "100%",
        justifyContent: align === "center" ? "center" : align === "right" ? "flex-end" : "flex-start",
      }}
    >
      {label}
    </TableSortLabel>
  );
}

/**
 * Server-driven TanStack table for admin consultations (current page only).
 */
export default function ConsultationsTable({
  rows,
  page = 1,
  totalPages = 1,
  onPageChange,
  loading,
  statusFilter,
  statusOptions,
  sortField,
  sortDir,
  onSortChange,
  view,
  viewDocument,
  viewComment,
  openModal,
  asssonpossible,
  UpdateDetails,
  meetinglog,
  handleChangestatus,
  handleGeneratePartnersXmlReport,
  openClientProfile,
  openDoctorProfile,
}) {
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [statusRowData, setStatusRowData] = useState(null);
  const [confirmStatusOpen, setConfirmStatusOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [neverAskStatusChange, setNeverAskStatusChange] = useState(
    () => localStorage.getItem("consultations_skip_status_confirm") === "1"
  );
  const [neverAskChecked, setNeverAskChecked] = useState(false);
  const [dismissingRowIds, setDismissingRowIds] = useState([]);
  const [hiddenRowIds, setHiddenRowIds] = useState([]);
  const [undoToast, setUndoToast] = useState(null);
  const [pendingUndoAction, setPendingUndoAction] = useState(null);
  const [highlightedRowIds, setHighlightedRowIds] = useState([]);
  const [undoFocusRowId, setUndoFocusRowId] = useState(null);
  /** Meeting id of the row selected via keyboard 1–9 / 0 (0 = 10th row). */
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [lastCopiedValue, setLastCopiedValue] = useState("");
  const [fileCountByMeeting, setFileCountByMeeting] = useState({});
  const rowRefs = useRef({});
  const tableContainerRef = useRef(null);
  const requestedFileCountsRef = useRef(new Set());

  const openStatusMenu = (event, data) => {
    setStatusAnchorEl(event.currentTarget);
    setStatusRowData(data);
  };

  const closeStatusMenu = () => {
    setStatusAnchorEl(null);
    setStatusRowData(null);
  };

  const handleStatusSelection = (rowData, nextStatus) => {
    if (!rowData?._id || rowData?.statuses === nextStatus) return;
    if (neverAskStatusChange) {
      const fromStatus = rowData?.statuses;
      const toStatus = nextStatus;
      const shouldDisappear = shouldDisappearFromCurrentFilter(toStatus);
      const runChange = () => applyStatusChange(rowData._id, toStatus);
      if (shouldDisappear) {
        animateRowOutWithUndo({
          rowId: rowData._id,
          fromStatus,
          toStatus,
        });
      } else {
        runChange();
      }
    } else {
      setPendingStatusChange({
        meetingId: rowData._id,
        fromStatus: rowData?.statuses,
        toStatus: nextStatus,
      });
      setConfirmStatusOpen(true);
    }
  };

  const applyStatusChange = (meetingId, nextStatus) => {
    handleChangestatus({ target: { value: nextStatus } }, meetingId);
  };

  const copyToClipboard = async (value) => {
    const text = String(value ?? "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setLastCopiedValue(text);
      setTimeout(() => setLastCopiedValue(""), 1200);
    } catch (err) {
      // no-op fallback; keep silent to avoid noisy toasts during repetitive work
    }
  };

  const CopyableText = ({ value, children, ariaLabel, maxWidth }) => (
    <Tooltip title={lastCopiedValue === String(value) ? "Copied" : "Click to copy"}>
      <Box
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={() => copyToClipboard(value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            copyToClipboard(value);
          }
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.6,
          maxWidth: maxWidth || "100%",
          cursor: "copy",
          borderRadius: "8px",
          px: 0.35,
          py: 0.15,
          "& .copy-icon": {
            opacity: 0,
            transform: "translateY(1px)",
            transition: "all 140ms ease",
          },
          "&:hover .copy-icon, &:focus-visible .copy-icon": {
            opacity: 0.95,
            animation: `${copyIconPulse} 160ms ease-out`,
          },
          "&:hover": { bgcolor: "rgba(148,163,184,0.12)" },
          "&:focus-visible": {
            outline: "2px solid rgba(14,165,233,0.45)",
            outlineOffset: "2px",
          },
        }}
      >
        <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {children || value}
        </Box>
        <ContentCopyIcon className="copy-icon" sx={{ fontSize: 14, color: "text.secondary" }} />
      </Box>
    </Tooltip>
  );

  const performUndoStatusChange = () => {
    if (!pendingUndoAction?.rowId) return;
    setHiddenRowIds((prev) => prev.filter((id) => id !== pendingUndoAction.rowId));
    if (pendingUndoAction.fromStatus) {
      applyStatusChange(pendingUndoAction.rowId, pendingUndoAction.fromStatus);
    }
    setUndoFocusRowId(pendingUndoAction.rowId);
    setPendingUndoAction(null);
    setUndoToast(null);
  };

  const animateRowOutWithUndo = ({ rowId, fromStatus, toStatus }) => {
    setDismissingRowIds((prev) => [...prev, rowId]);
    setTimeout(() => {
      setHiddenRowIds((prev) => [...prev, rowId]);
      setDismissingRowIds((prev) => prev.filter((id) => id !== rowId));
      applyStatusChange(rowId, toStatus);
    }, 360);
    setPendingUndoAction({
      rowId,
      fromStatus,
      toStatus,
      armedAt: Date.now(),
    });
    setUndoToast({
      open: true,
      rowId,
      fromStatus,
      toStatus,
      canUndo: false,
    });
  };

  useEffect(() => {
    if (!undoToast?.open) return;
    const tmr = setTimeout(() => {
      setUndoToast((prev) => (prev ? { ...prev, canUndo: true } : prev));
    }, 450);
    return () => clearTimeout(tmr);
  }, [undoToast?.open]);

  useEffect(() => {
    const activeRows = new Set(rows.map((r) => r["Meeting Id"]));
    setHiddenRowIds((prev) => prev.filter((id) => activeRows.has(id)));
    setDismissingRowIds((prev) => prev.filter((id) => activeRows.has(id)));
  }, [rows]);

  useEffect(() => {
    setHiddenRowIds([]);
    setDismissingRowIds([]);
  }, [statusFilter]);

  useEffect(() => {
    if (!pendingUndoAction?.rowId) return;
    const onPointerDown = (e) => {
      const clickedUndoControl = e.target?.closest?.("[data-undo-control='true']");
      if (clickedUndoControl) return;
      if (Date.now() - pendingUndoAction.armedAt < 450) return;
      setPendingUndoAction(null);
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => window.removeEventListener("pointerdown", onPointerDown, true);
  }, [pendingUndoAction]);

  useEffect(() => {
    const onUndoShortcut = (e) => {
      const isUndoCombo =
        (e.ctrlKey || e.metaKey) &&
        !e.shiftKey &&
        String(e.key).toLowerCase() === "z";
      if (!isUndoCombo) return;
      if (!pendingUndoAction?.rowId) return;
      if (undoToast?.open && !undoToast?.canUndo) return;
      e.preventDefault();
      performUndoStatusChange();
    };
    window.addEventListener("keydown", onUndoShortcut);
    return () => window.removeEventListener("keydown", onUndoShortcut);
  }, [pendingUndoAction, undoToast]);

  useEffect(() => {
    rows.forEach((r) => {
      const meetingId = r["Meeting Id"];
      if (!meetingId || requestedFileCountsRef.current.has(meetingId)) return;
      requestedFileCountsRef.current.add(meetingId);
      Promise.allSettled([getAllDocuments(meetingId), getMriDocuments(meetingId)])
        .then(([docsRes, mriRes]) => {
          const docsCount =
            docsRes.status === "fulfilled"
              ? docsRes.value?.data?.meetingDocuments?.length || 0
              : 0;
          const mriCount =
            mriRes.status === "fulfilled"
              ? mriRes.value?.data?.pacseeFiles?.length || 0
              : 0;
          setFileCountByMeeting((prev) => ({
            ...prev,
            [meetingId]: docsCount + mriCount,
          }));
        })
        .catch(() => {
          setFileCountByMeeting((prev) => ({ ...prev, [meetingId]: 0 }));
        });
    });
  }, [rows]);

  const shouldDisappearFromCurrentFilter = (nextStatus) => {
    if (!statusFilter || statusFilter.length === 0) return false;
    return !statusFilter.includes(nextStatus);
  };

  const closeConfirmStatusDialog = () => {
    setConfirmStatusOpen(false);
    setPendingStatusChange(null);
    setNeverAskChecked(false);
  };

  const statusTone = (status) => {
    const tones = {
      Scheduled: { bg: "rgba(59,130,246,0.14)", color: "#1d4ed8" },
      Created: { bg: "rgba(245,158,11,0.14)", color: "#b45309" },
      DatesOffered: { bg: "rgba(6,182,212,0.14)", color: "#0e7490" },
      AdminCare: { bg: "rgba(168,85,247,0.14)", color: "#7e22ce" },
      Done: { bg: "rgba(34,197,94,0.14)", color: "#15803d" },
      NoShow: { bg: "rgba(239,68,68,0.14)", color: "#b91c1c" },
      Canceled: { bg: "rgba(107,114,128,0.16)", color: "#374151" },
    };
    return tones[status] || tones.Created;
  };

  const renderOfferAction = (data, ms) => {
    const hasReq =
      data?.requestedDates != null &&
      data?.requestedDates?.length !== 0 &&
      !data?.ASAP;
    const disabled = !(ms === "Created" || ms === "DatesOffered");

    if (hasReq) {
      openModal(
        data._id,
        data?.requestedDates,
        data?.consultation?._id,
        data?.offerDates,
        ms,
        data?.requestedDates,
        data?.ASAP
      );
      return;
    }
    asssonpossible(
      data._id,
      data?.consultation?._id,
      data?.offerDates,
      ms,
      data?.requestedDates,
      data?.ASAP
    );
    return disabled;
  };

  /** Equal width for all status chips on this page (longest visible label). */
  const statusPillUniformWidthPx = useMemo(() => {
    let maxLen = 0;
    for (const r of rows) {
      if (hiddenRowIds.includes(r["Meeting Id"])) continue;
      const label = String(r.MeetingStatus ?? "");
      if (label.length > maxLen) maxLen = label.length;
    }
    if (maxLen === 0) maxLen = 11;
    const px = Math.min(Math.round(7.4 * maxLen + 34), 168);
    return Math.max(px, 96);
  }, [rows, hiddenRowIds]);

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "rowIndex",
        meta: { align: "center", minWidth: 44, width: "3%" },
        header: "#",
        cell: (info) => (
          <Typography
            fontWeight={700}
            fontSize={13}
            color="text.secondary"
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {info.row.index + 1}
          </Typography>
        ),
      }),
      columnHelper.accessor("Client Name", {
        meta: { align: "left", minWidth: 128, width: "13%" },
        header: "Patient",
        cell: (info) => {
          const row = info.row.original;
          const data = row.data;
          const clientName = info.getValue() || "Unknown client";
          const clientId = data?.clientData?._id;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                maxWidth: 220,
              }}
            >
              <Typography
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  if (clientId) openClientProfile?.(data);
                }}
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: clientId ? "pointer" : "default",
                  color: clientId ? "primary.main" : "text.primary",
                  "&:hover": clientId
                    ? { textDecoration: "underline" }
                    : undefined,
                }}
              >
                {clientName}
              </Typography>
              <Tooltip
                title={
                  lastCopiedValue === String(clientName).trim()
                    ? "Copied"
                    : "Copy"
                }
              >
                <IconButton
                  size="small"
                  aria-label="Copy patient name"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(clientName);
                  }}
                  sx={{ p: 0.35, flexShrink: 0 }}
                >
                  <ContentCopyIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      }),
      columnHelper.accessor("Physician Name", {
        meta: { align: "left", minWidth: 128, width: "13%" },
        header: "Doctor",
        cell: (info) => {
          const row = info.row.original;
          const data = row.data;
          const physicianDisplay = info.getValue() || "Unassigned physician";
          const physicianCopyValue = info.getValue() || "Unassigned physician";
          const doctorId =
            data?.doctorData?._id ||
            (data?.offerDates?.[0]?.DoctorId
              ? data.offerDates[0].DoctorId
              : null);
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                maxWidth: 220,
              }}
            >
              <Typography
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  if (doctorId) openDoctorProfile?.(data);
                }}
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: 1.3,
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: doctorId ? "pointer" : "default",
                  color: "text.primary",
                  "&:hover": doctorId
                    ? { textDecoration: "underline", color: "primary.main" }
                    : undefined,
                }}
              >
                {physicianDisplay}
              </Typography>
              <Tooltip
                title={
                  lastCopiedValue === String(physicianCopyValue).trim()
                    ? "Copied"
                    : "Copy"
                }
              >
                <IconButton
                  size="small"
                  aria-label="Copy doctor name"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(physicianCopyValue);
                  }}
                  sx={{ p: 0.35, flexShrink: 0 }}
                >
                  <ContentCopyIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      }),
      columnHelper.display({
        id: "meetingAndSpecialty",
        meta: { align: "left", minWidth: 140, width: "14%" },
        header: () => (
          <Box component="span" sx={{ lineHeight: 1.25 }}>
            <Typography component="span" display="block" fontWeight={600} fontSize={13}>
              Meeting
            </Typography>
            <Typography
              component="span"
              display="block"
              fontWeight={500}
              fontSize={11}
              color="text.secondary"
            >
              Consultation
            </Typography>
          </Box>
        ),
        cell: (info) => {
          const row = info.row.original;
          const data = row.data;
          const meetingIdStr = String(row["Meeting Id"] ?? "");
          const idLine = `ID ${meetingIdStr}`;
          const specialty =
            data?.consultation?.eng || row.consultingField || "—";
          return (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Typography
                  fontSize={12}
                  color="text.secondary"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {idLine}
                </Typography>
                <Tooltip
                  title={
                    lastCopiedValue === meetingIdStr.trim()
                      ? "Copied"
                      : "Copy"
                  }
                >
                  <IconButton
                    size="small"
                    aria-label="Copy meeting id"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(meetingIdStr);
                    }}
                    sx={{ p: 0.35, flexShrink: 0 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.35,
                }}
              >
                <Typography
                  fontSize={12}
                  color="text.secondary"
                  sx={{
                    opacity: 0.85,
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {specialty}
                </Typography>
                <Tooltip
                  title={
                    lastCopiedValue === String(specialty).trim()
                      ? "Copied"
                      : "Copy"
                  }
                >
                  <IconButton
                    size="small"
                    aria-label="Copy specialty"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(specialty);
                    }}
                    sx={{ p: 0.35, flexShrink: 0 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          );
        },
      }),
      columnHelper.accessor("Schedule Date", {
        meta: { align: "left", minWidth: 156, width: "17%" },
        header: () => (
          <SortableHeader
            label="Schedule Date"
            field="scheduledDate"
            sortField={sortField}
            sortDir={sortDir}
            onSortChange={onSortChange}
            align="left"
          />
        ),
        cell: (info) => {
          const v = info.getValue();
          const created = info.row.original["Created Date"];
          return (
            <Box sx={{ textAlign: "left" }}>
              <Typography fontSize={13} fontWeight={500} lineHeight={1.35}>
                <CopyableText
                  value={v ? moment(v).format("DD MMM YYYY, HH:mm") : "-"}
                  ariaLabel="Copy scheduled date"
                >
                  {v ? moment(v).format("DD MMM YYYY, HH:mm") : "-"}
                </CopyableText>
              </Typography>
              <Typography
                fontSize={12}
                color="text.secondary"
                sx={{ opacity: 0.7, mt: 0.35, lineHeight: 1.35 }}
              >
                <CopyableText
                  value={`Created: ${
                    created ? moment(created).format("DD MMM YYYY") : "-"
                  }`}
                  ariaLabel="Copy created date"
                >
                  Created: {created ? moment(created).format("DD MMM YYYY") : "-"}
                </CopyableText>
              </Typography>
            </Box>
          );
        },
      }),
      columnHelper.accessor("MeetingStatus", {
        id: "statusAndActions",
        meta: { align: "right", minWidth: 360, width: "40%" },
        header: () => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
              width: "100%",
              gap: 2,
              flexWrap: "nowrap",
            }}
          >
            <SortableHeader
              label="Meeting status"
              field="statuses"
              sortField={sortField}
              sortDir={sortDir}
              onSortChange={onSortChange}
              align="left"
            />
            <Typography
              component="span"
              sx={{
                fontWeight: 600,
                fontSize: 13,
                whiteSpace: "nowrap",
                flexShrink: 0,
                lineHeight: 1.2,
              }}
            >
              Actions
            </Typography>
          </Box>
        ),
        cell: (info) => {
          const row = info.row.original;
          const data = row.data;
          const tone = statusTone(info.getValue());
          const comments = data?.comments;
          const hasNotes = Boolean(
            comments != null && String(comments).trim() !== ""
          );
          const offerDatesDisabled = !(
            data?.statuses === "Created" || data?.statuses === "DatesOffered"
          );
          const showXmlReport =
            data?.statuses === "Done" &&
            data?.partnerId?.some((el) => String(el) === PARTNER_ICHILOV);

          const actionBtnSx = {
            borderRadius: "10px",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "rgba(255,255,255,0.62)",
            "&:hover": {
              bgcolor: "rgba(240,249,255,0.9)",
              borderColor: "rgba(56,189,248,0.45)",
            },
            "&.Mui-disabled": {
              opacity: 0.38,
              borderColor: "divider",
            },
          };

          const notesTooltip = hasNotes
            ? String(comments).length > 140
              ? `${String(comments).slice(0, 140)}…`
              : String(comments)
            : "Admin notes (empty) — click to add";

          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1.25,
                width: "100%",
                minWidth: 0,
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <Tooltip title="Update status">
                <Chip
                  label={info.getValue()}
                  clickable
                  onClick={(e) => openStatusMenu(e, data)}
                  sx={{
                    height: 30,
                    boxSizing: "border-box",
                    minWidth: { xs: "100%", sm: statusPillUniformWidthPx },
                    width: { xs: "100%", sm: statusPillUniformWidthPx },
                    maxWidth: { xs: "100%", sm: statusPillUniformWidthPx },
                    borderRadius: "14px",
                    bgcolor: tone.bg,
                    color: tone.color,
                    border: "1px solid rgba(255,255,255,0.45)",
                    backdropFilter: "blur(10px)",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    flexShrink: 0,
                    justifyContent: "center",
                    "& .MuiChip-label": {
                      px: 1.25,
                      textAlign: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                    },
                    "&:hover": { boxShadow: "0 4px 14px rgba(15,23,42,0.12)" },
                  }}
                />
              </Tooltip>
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              justifyContent="flex-end"
              alignItems="center"
              sx={{ rowGap: 0.5, flexShrink: 0 }}
            >
              <Tooltip title="Documents / Imaging">
                <Badge
                  badgeContent={fileCountByMeeting[data?._id] ?? 0}
                  color="error"
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: 10,
                      minWidth: 16,
                      height: 16,
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label={`Open files for meeting ${data?._id}`}
                    onClick={() =>
                      viewDocument(
                        data._id,
                        data.clientData?._id,
                        data,
                        "view_or_upload"
                      )
                    }
                    sx={actionBtnSx}
                  >
                    <FolderOpenOutlinedIcon fontSize="small" />
                  </IconButton>
                </Badge>
              </Tooltip>
              <Tooltip title="Update details">
                <IconButton
                  size="small"
                  aria-label="Update meeting details"
                  onClick={() =>
                    view(
                      data._id,
                      data.consultation?._id,
                      data,
                      data?.offerDates
                    )
                  }
                  sx={actionBtnSx}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  offerDatesDisabled
                    ? "Offer dates (only for Created or Dates offered)"
                    : "Offer dates"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    aria-label="Offer dates"
                    disabled={offerDatesDisabled}
                    onClick={() => renderOfferAction(data, data.statuses)}
                    sx={actionBtnSx}
                  >
                    <CalendarMonthOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Meeting log">
                <IconButton
                  size="small"
                  aria-label="Meeting log"
                  onClick={() => meetinglog(data._id)}
                  sx={actionBtnSx}
                >
                  <AssignmentOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={notesTooltip}>
                <Badge
                  variant="dot"
                  color="error"
                  invisible={!hasNotes}
                  overlap="circular"
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  sx={{
                    "& .MuiBadge-badge": {
                      minWidth: 8,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      boxShadow: "0 0 0 1.5px rgba(255,255,255,0.95)",
                    },
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label="Admin notes"
                    onClick={(e) => {
                      e.stopPropagation();
                      viewComment(data._id, comments);
                    }}
                    sx={actionBtnSx}
                  >
                    <StickyNote2OutlinedIcon fontSize="small" />
                  </IconButton>
                </Badge>
              </Tooltip>
              <Tooltip
                title={
                  data?.isNotification
                    ? "Disable notifications"
                    : "Enable notifications"
                }
              >
                <IconButton
                  size="small"
                  aria-label={
                    data?.isNotification
                      ? "Disable notifications"
                      : "Enable notifications"
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    UpdateDetails(!data?.isNotification, data._id);
                  }}
                  sx={{
                    ...actionBtnSx,
                    color: data?.isNotification ? "success.main" : "warning.main",
                    borderColor: data?.isNotification
                      ? "rgba(34, 197, 94, 0.42)"
                      : "rgba(249, 115, 22, 0.5)",
                    bgcolor: data?.isNotification
                      ? "rgba(220, 252, 231, 0.55)"
                      : "rgba(255, 237, 213, 0.55)",
                    "&:hover": {
                      bgcolor: data?.isNotification
                        ? "rgba(187, 247, 208, 0.88)"
                        : "rgba(254, 215, 170, 0.88)",
                      borderColor: data?.isNotification
                        ? "rgba(22, 163, 74, 0.55)"
                        : "rgba(234, 88, 12, 0.55)",
                    },
                  }}
                >
                  {data?.isNotification ? (
                    <NotificationsOffOutlinedIcon fontSize="small" />
                  ) : (
                    <NotificationsOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              {showXmlReport ? (
                <Tooltip title="XML report (Ichilov)">
                  <IconButton
                    size="small"
                    aria-label="Download XML report"
                    onClick={() => handleGeneratePartnersXmlReport(data._id)}
                    sx={actionBtnSx}
                  >
                    <ArticleOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
            </Box>
          );
        },
      }),
    ],
    [
      statusOptions,
      sortField,
      sortDir,
      onSortChange,
      statusPillUniformWidthPx,
      view,
      viewDocument,
      viewComment,
      openModal,
      asssonpossible,
      UpdateDetails,
      meetinglog,
      handleChangestatus,
      handleGeneratePartnersXmlReport,
      openClientProfile,
      openDoctorProfile,
    ]
  );

  const visibleRows = useMemo(
    () => rows.filter((r) => !hiddenRowIds.includes(r["Meeting Id"])),
    [rows, hiddenRowIds]
  );

  const openStatusMenuFromKeyboard = useCallback(
    (meetingIdStr) => {
      const rowEl = rowRefs.current[meetingIdStr];
      const row = visibleRows.find((r) => String(r["Meeting Id"]) === meetingIdStr);
      if (!rowEl || !row?.data) return;
      setStatusAnchorEl(rowEl);
      setStatusRowData(row.data);
    },
    [visibleRows]
  );

  const runRowActionKey = useCallback(
    (keyLower) => {
      let meetingIdStr = selectedMeetingId;
      if (!meetingIdStr && visibleRows.length > 0) {
        meetingIdStr = String(visibleRows[0]["Meeting Id"]);
        setSelectedMeetingId(meetingIdStr);
      }
      if (!meetingIdStr) return;
      const row = visibleRows.find((r) => String(r["Meeting Id"]) === meetingIdStr);
      const data = row?.data;
      if (!data) return;

      switch (keyLower) {
        case "a":
          openStatusMenuFromKeyboard(meetingIdStr);
          break;
        case "s":
          viewDocument(data._id, data.clientData?._id, data, "view_or_upload");
          break;
        case "d":
          view(data._id, data.consultation?._id, data, data?.offerDates);
          break;
        case "f":
          renderOfferAction(data, data.statuses);
          break;
        case "g":
          meetinglog(data._id);
          break;
        case "h":
          viewComment(data._id, data?.comments);
          break;
        case "j":
          UpdateDetails(!data?.isNotification, data._id);
          break;
        case "k": {
          const showXml =
            data?.statuses === "Done" &&
            data?.partnerId?.some((el) => String(el) === PARTNER_ICHILOV);
          if (showXml) handleGeneratePartnersXmlReport(data._id);
          break;
        }
        default:
          break;
      }
    },
    [
      selectedMeetingId,
      visibleRows,
      openStatusMenuFromKeyboard,
      viewDocument,
      view,
      viewComment,
      meetinglog,
      UpdateDetails,
      handleGeneratePartnersXmlReport,
      renderOfferAction,
    ]
  );

  const table = useReactTable({
    data: visibleRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    setSelectedMeetingId(null);
  }, [page]);

  useEffect(() => {
    if (
      selectedMeetingId != null &&
      !visibleRows.some((r) => String(r["Meeting Id"]) === selectedMeetingId)
    ) {
      setSelectedMeetingId(null);
    }
  }, [visibleRows, selectedMeetingId]);

  const handleStatusMenuListKeyDown = useCallback(
    (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const key = e.key.toLowerCase();
      const idx = STATUS_MENU_KEYS.indexOf(key);
      if (idx === -1) return;
      if (idx >= statusOptions.length) return;
      const opt = statusOptions[idx];
      if (!statusRowData || statusRowData.statuses === opt.type) return;
      e.preventDefault();
      e.stopPropagation();
      handleStatusSelection(statusRowData, opt.type);
      closeStatusMenu();
    },
    [statusOptions, statusRowData, handleStatusSelection, closeStatusMenu]
  );

  useEffect(() => {
    const blockedTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        el.isContentEditable
      ) {
        return true;
      }
      if (
        el.closest?.('[role="dialog"]') ||
        el.closest?.('[aria-modal="true"]')
      ) {
        return true;
      }
      if (el.closest?.('[role="menu"]') || el.closest?.('[role="listbox"]')) {
        return true;
      }
      return false;
    };

    const onKeyDown = (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (confirmStatusOpen) return;

      const keyLower = event.key.toLowerCase();
      if (statusAnchorEl && STATUS_MENU_KEYS.includes(keyLower)) {
        handleStatusMenuListKeyDown(event);
        return;
      }

      if (blockedTarget(event.target)) return;

      if (event.key === "ArrowLeft" && typeof onPageChange === "function" && page > 1) {
        event.preventDefault();
        event.stopPropagation();
        onPageChange(page - 1);
        return;
      }
      if (
        event.key === "ArrowRight" &&
        typeof onPageChange === "function" &&
        page < totalPages
      ) {
        event.preventDefault();
        event.stopPropagation();
        onPageChange(page + 1);
        return;
      }

      if (event.key === "ArrowDown") {
        if (visibleRows.length === 0) return;
        event.preventDefault();
        event.stopPropagation();
        const idx = selectedMeetingId
          ? visibleRows.findIndex((r) => String(r["Meeting Id"]) === selectedMeetingId)
          : -1;
        const next = idx < 0 ? 0 : Math.min(idx + 1, visibleRows.length - 1);
        setSelectedMeetingId(String(visibleRows[next]["Meeting Id"]));
        return;
      }
      if (event.key === "ArrowUp") {
        if (visibleRows.length === 0) return;
        event.preventDefault();
        event.stopPropagation();
        const idx = selectedMeetingId
          ? visibleRows.findIndex((r) => String(r["Meeting Id"]) === selectedMeetingId)
          : visibleRows.length;
        const next = idx <= 0 ? 0 : idx - 1;
        setSelectedMeetingId(String(visibleRows[next]["Meeting Id"]));
        return;
      }

      if (/^[0-9]$/.test(event.key)) {
        if (visibleRows.length === 0) return;
        const slot = event.key === "0" ? 9 : parseInt(event.key, 10) - 1;
        const idx = Math.min(Math.max(slot, 0), visibleRows.length - 1);
        event.preventDefault();
        event.stopPropagation();
        setSelectedMeetingId(String(visibleRows[idx]["Meeting Id"]));
        return;
      }

      if (/^[asdfghjk]$/i.test(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        runRowActionKey(event.key.toLowerCase());
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [
    visibleRows,
    selectedMeetingId,
    page,
    totalPages,
    onPageChange,
    confirmStatusOpen,
    statusAnchorEl,
    runRowActionKey,
    handleStatusMenuListKeyDown,
  ]);

  useEffect(() => {
    if (!selectedMeetingId) return;
    const idx = visibleRows.findIndex(
      (r) => String(r["Meeting Id"]) === selectedMeetingId
    );
    if (idx === 0) {
      tableContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const rowEl = rowRefs.current[selectedMeetingId];
    if (!rowEl) return;
    rowEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedMeetingId, visibleRows]);

  useEffect(() => {
    if (!undoFocusRowId) return;
    const rowEl =
      rowRefs.current[String(undoFocusRowId)] ??
      rowRefs.current[undoFocusRowId];
    if (!rowEl) return;
    rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
    rowEl.focus({ preventScroll: true });
    setHighlightedRowIds((prev) =>
      prev.includes(undoFocusRowId) ? prev : [...prev, undoFocusRowId]
    );
    const clearTmr = setTimeout(() => {
      setHighlightedRowIds((prev) => prev.filter((id) => id !== undoFocusRowId));
    }, 2200);
    const focusTmr = setTimeout(() => setUndoFocusRowId(null), 300);
    return () => {
      clearTimeout(clearTmr);
      clearTimeout(focusTmr);
    };
  }, [undoFocusRowId, visibleRows]);

  const consultationMenuPaperProps = {
    elevation: 0,
    sx: {
      mt: 0.5,
      borderRadius: "12px",
      border: "1px solid rgba(148, 163, 184, 0.22)",
      boxShadow: "0 12px 28px rgba(15,23,42,0.1)",
      overflow: "hidden",
      bgcolor: "rgba(255,255,255,0.97)",
      backdropFilter: "blur(12px)",
      minWidth: 220,
      outline: "none",
    },
  };

  const consultationMenuItemSx = {
    borderRadius: "10px",
    mx: 0.75,
    my: 0.25,
    minHeight: 40,
    fontSize: 13,
    fontWeight: 600,
    gap: 1,
    "&:hover": { bgcolor: "rgba(241, 245, 249, 0.95)" },
    "&.Mui-disabled": { opacity: 0.45 },
  };

  if (loading && visibleRows.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
    <TableContainer
      ref={tableContainerRef}
      component={Paper}
      elevation={0}
      sx={{
        maxHeight: "72vh",
        borderRadius: 3,
        border: "1px solid rgba(255,255,255,0.45)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.76), rgba(255,255,255,0.62))",
        backdropFilter: "blur(14px)",
        boxShadow: "0 12px 28px rgba(15,23,42,0.08)",
      }}
    >
      <Table
        stickyHeader
        size="medium"
        sx={{
          minWidth: 1180,
          tableLayout: "fixed",
          width: "100%",
        }}
      >
        <TableHead>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((header) => {
                const colMeta = header.column.columnDef.meta ?? {};
                return (
                <TableCell
                  key={header.id}
                  align={colMeta.align ?? "left"}
                  sx={{
                    fontWeight: 600,
                    fontSize: 13,
                    bgcolor: "rgba(248,250,252,0.86)",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid rgba(148,163,184,0.22)",
                    minWidth: colMeta.minWidth,
                    width: colMeta.width,
                    verticalAlign: "bottom",
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableCell>
              );})}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                <Typography color="text.secondary" py={3}>
                  No meetings match the current filters.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row, idx) => {
              const meetingIdStr = String(row.original["Meeting Id"]);
              const isRowSelected =
                selectedMeetingId != null && selectedMeetingId === meetingIdStr;
              return (
              <TableRow
                key={row.id}
                ref={(el) => {
                  const key = String(row.original["Meeting Id"]);
                  if (el) {
                    rowRefs.current[key] = el;
                  } else {
                    delete rowRefs.current[key];
                  }
                }}
                tabIndex={-1}
                hover
                aria-selected={isRowSelected}
                data-selected={isRowSelected ? "true" : undefined}
                data-keyboard-selected={isRowSelected ? "true" : undefined}
                onClick={(e) => {
                  if (
                    e.target.closest(
                      "button, a, [role='button'], input, textarea, select, [role='menuitem']"
                    )
                  ) {
                    return;
                  }
                  setSelectedMeetingId(meetingIdStr);
                }}
                sx={{
                  cursor: "pointer",
                  bgcolor: idx % 2 === 0 ? "background.paper" : "grey.50",
                  "&:hover": {
                    bgcolor: "rgba(241,245,249,0.86)",
                    transform: "translateY(-1px)",
                  },
                  ...(highlightedRowIds.includes(row.original["Meeting Id"])
                    ? {
                        animation: `${undoPulse} 850ms ease-out 2`,
                        boxShadow: "inset 0 0 0 1px rgba(14,165,233,0.2)",
                        bgcolor: "rgba(224,242,254,0.36)",
                      }
                    : null),
                  ...(dismissingRowIds.includes(row.original["Meeting Id"])
                    ? {
                        opacity: 0,
                        transform: "scale(0.93) translateY(-6px)",
                        filter: "blur(3px)",
                      }
                    : null),
                  ...(isRowSelected
                    ? {
                        outline: "2px solid rgba(37, 99, 235, 0.55)",
                        outlineOffset: "-2px",
                        bgcolor: "rgba(239, 246, 255, 0.92)",
                        boxShadow:
                          "inset 0 0 0 1px rgba(59, 130, 246, 0.22)",
                      }
                    : null),
                  transition: "all 360ms cubic-bezier(0.22, 1, 0.36, 1)",
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const colMeta = cell.column.columnDef.meta ?? {};
                  return (
                  <TableCell
                    key={cell.id}
                    align={colMeta.align ?? "left"}
                    sx={{
                      py: 1.8,
                      fontSize: 13,
                      verticalAlign: "middle",
                      borderBottom: "1px solid rgba(148,163,184,0.18)",
                      minWidth: colMeta.minWidth,
                      width: colMeta.width,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                );})}
              </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
    <Menu
      dir="ltr"
      anchorEl={statusAnchorEl}
      open={Boolean(statusAnchorEl)}
      onClose={closeStatusMenu}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{
        paper: consultationMenuPaperProps,
        root: { sx: { "& .MuiBackdrop-root": { bgcolor: "transparent" } } },
      }}
      MenuListProps={{
        sx: { py: 0.75, px: 0.25 },
        autoFocusItem: true,
      }}
    >
      {statusOptions.map((d, index) => {
        const tone = statusTone(d.type);
        const letter = STATUS_MENU_KEYS[index]?.toUpperCase();
        return (
          <MenuItem
            key={d.type}
            disabled={statusRowData?.statuses === d.type}
            onClick={() => {
              handleStatusSelection(statusRowData, d.type);
              closeStatusMenu();
            }}
            sx={{
              ...consultationMenuItemSx,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  flexShrink: 0,
                  bgcolor: tone.bg,
                  border: `1px solid ${tone.color}40`,
                }}
              />
              Set to {d.type}
            </Box>
            {letter ? (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  flexShrink: 0,
                  opacity: 0.65,
                  fontWeight: 700,
                }}
              >
                {letter}
              </Typography>
            ) : null}
          </MenuItem>
        );
      })}
    </Menu>
    <Dialog
      dir="ltr"
      open={confirmStatusOpen}
      onClose={closeConfirmStatusDialog}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Confirm status change</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1.25 }}>
          Change meeting status
          {pendingStatusChange?.fromStatus
            ? ` from "${pendingStatusChange.fromStatus}" to "${pendingStatusChange?.toStatus}"?`
            : "?"}
        </DialogContentText>
        <FormControlLabel
          control={
            <Checkbox
              checked={neverAskChecked}
              onChange={(e) => setNeverAskChecked(e.target.checked)}
            />
          }
          label="Never show again"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeConfirmStatusDialog} variant="text">
          Cancel
        </Button>
        <Button
          onClick={() => {
            const rowId = pendingStatusChange?.meetingId;
            const fromStatus = pendingStatusChange?.fromStatus;
            const toStatus = pendingStatusChange?.toStatus;
            const shouldDisappear = shouldDisappearFromCurrentFilter(toStatus);
            if (rowId && toStatus) {
              if (shouldDisappear) {
                closeConfirmStatusDialog();
                setTimeout(() => {
                  animateRowOutWithUndo({ rowId, fromStatus, toStatus });
                }, 220);
              } else {
                closeConfirmStatusDialog();
                setTimeout(() => {
                  applyStatusChange(rowId, toStatus);
                }, 220);
              }
            } else {
              closeConfirmStatusDialog();
            }
            if (neverAskChecked) {
              localStorage.setItem("consultations_skip_status_confirm", "1");
              setNeverAskStatusChange(true);
            }
          }}
          variant="contained"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
    <Snackbar
      ContentProps={{ sx: { direction: "ltr" } }}
      open={Boolean(undoToast?.open)}
      autoHideDuration={2600}
      TransitionComponent={Slide}
      TransitionProps={{ direction: "up" }}
      onClose={() => setUndoToast(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      message={
        undoToast
          ? `Changed status from ${undoToast.fromStatus} to ${undoToast.toStatus}`
          : ""
      }
      action={
        <Button
          data-undo-control="true"
          color="inherit"
          size="small"
          disabled={!undoToast?.canUndo || !pendingUndoAction?.rowId}
          onClick={performUndoStatusChange}
        >
          Undo
        </Button>
      }
    />
    </>
  );
}
